import React, { useEffect, useLayoutEffect, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { IconArrowRight, IconCreditCard } from "@tabler/icons-react"
import type { IChapterGivingAccount } from "@dc-ireland/shared"
import { useFeatureFlags } from "../../context/FeatureFlagsProvider"
import apiClient from "../../lib/api"
import { getChapterMeta } from "../../lib/chapters"
import { giveConfig, givingMethodsLabel, hasStripePayment } from "../../lib/giveConfig"
import LandingNav from "./LandingNav"
import LandingFooter from "./LandingFooter"
import LandingReveal from "./LandingReveal"
import GiveChapterDetailsModal from "./GiveChapterDetailsModal"

type GiveOptionProps = {
  icon: React.ReactNode
  title: string
  description: string
  onContinue: () => void
  delay?: number
}

const GiveOption: React.FC<GiveOptionProps> = ({ icon, title, description, onContinue, delay = 0 }) => (
  <LandingReveal delay={delay}>
    <article className="flex h-full flex-col items-center gap-5 rounded-2xl border border-[#ebebeb] bg-white px-6 py-8 text-center shadow-[0_8px_30px_-16px_rgba(0,43,122,0.1)] transition duration-300 hover:border-dc-primary-900/15 hover:shadow-[0_16px_40px_-20px_rgba(0,43,122,0.14)]">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#f4f7ff] text-dc-primary-900">
        {icon}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="font-outfit text-xl font-semibold tracking-tight text-[#1f1f1f]">{title}</h3>
        <p className="font-dm-sans text-sm leading-6 text-[#5a5a5a]">{description}</p>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="mt-auto inline-flex items-center gap-1.5 font-dm-sans text-sm font-semibold text-dc-primary-900 transition hover:gap-2.5"
      >
        Continue
        <IconArrowRight size={16} stroke={2} />
      </button>
    </article>
  </LandingReveal>
)

const GivePage: React.FC = () => {
  const prefersReducedMotion = useReducedMotion()
  const { flags } = useFeatureFlags()
  const stripeEnabled = flags.stripeGiving
  const [accounts, setAccounts] = useState<IChapterGivingAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<IChapterGivingAccount | null>(null)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await apiClient.getGivingAccounts()
        if (!cancelled) setAccounts(data)
      } catch (err) {
        console.error("Failed to load giving accounts:", err)
        if (!cancelled) setAccounts([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleStripeContinue = () => {
    if (hasStripePayment()) {
      window.open(giveConfig.stripeUrl, "_blank", "noopener")
      return
    }

    window.location.href = `mailto:${giveConfig.contactEmail}?subject=Card%20Payment`
  }

  return (
    <div className="bg-[#fafafa]">
      <LandingNav />

      <section className="relative flex min-h-[42vh] items-end overflow-hidden bg-[#0a0a12] pb-14 pt-24 md:min-h-[46vh] md:pb-16 md:pt-28">
        <img
          src="/spiritual-growth.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-45"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#001a4d]/92 via-[#0d0d0d]/75 to-[#0d0d0d]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,rgba(158,126,67,0.28),transparent_55%)]" />

        {!prefersReducedMotion && (
          <motion.div
            className="pointer-events-none absolute -right-16 top-16 h-64 w-64 rounded-full bg-dc-primary-900/25 blur-[90px]"
            animate={{ x: [0, -20, 0], y: [0, 14, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        <div className="relative z-10 mx-auto w-full max-w-2xl px-4 text-center md:px-8 lg:px-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-3 font-dm-sans text-xs font-semibold uppercase tracking-[0.22em] text-[#9e7e43]"
          >
            Give
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="font-outfit text-4xl font-semibold tracking-tight text-white md:text-5xl"
          >
            Partner with the mission
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-4 max-w-lg font-dm-sans text-base leading-7 text-white/75"
          >
            Your gift helps us reach more people with hope, build community, and raise leaders across Ireland.
          </motion.p>
          <motion.blockquote
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-6 max-w-xl font-outfit text-lg font-medium leading-snug tracking-tight text-white/90 md:text-xl md:leading-[1.4]"
          >
            &ldquo;Each of you should give what you have decided in your heart to give, not reluctantly or under
            compulsion, for God loves a cheerful giver.&rdquo;
          </motion.blockquote>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-dm-sans text-sm tracking-wide text-white/70"
          >
            2 Corinthians 9:7
          </motion.p>
        </div>
      </section>

      {stripeEnabled && (
        <section className="px-4 pt-14 md:px-8 md:pt-16 lg:px-20">
          <div className="mx-auto grid max-w-md gap-6">
            <GiveOption
              icon={<IconCreditCard size={24} stroke={1.5} />}
              title="Card Payment"
              description="Give securely using your debit or credit card."
              delay={0}
              onContinue={handleStripeContinue}
            />
          </div>
        </section>
      )}

      <section className="px-4 pb-20 pt-14 md:px-8 md:pb-28 md:pt-16 lg:px-20">
        <div className="mx-auto max-w-2xl">
          <LandingReveal className="mb-10 text-center md:mb-12">
            <p className="mb-3 font-dm-sans text-xs font-semibold uppercase tracking-[0.2em] text-[#9e7e43]">
              Chapters
            </p>
            <h2 className="font-outfit text-3xl font-semibold tracking-tight text-[#1f1f1f] md:text-4xl">
              Choose where to give
            </h2>
            <p className="mx-auto mt-3 max-w-lg font-dm-sans text-base leading-7 text-[#5a5a5a]">
              Select a chapter to view bank transfer, PayPal, and SumUp details.
            </p>
          </LandingReveal>

          {loading ? (
            <div className="space-y-3" aria-busy="true" aria-label="Loading chapters">
              {[0, 1].map((i) => (
                <div key={i} className="h-[72px] animate-pulse rounded-xl bg-[#ebebeb]/70" />
              ))}
            </div>
          ) : accounts.length === 0 ? (
            <LandingReveal>
              <p className="rounded-xl border border-dashed border-[#ebebeb] px-5 py-8 text-center font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                Giving details are being prepared. Please check back soon, or email{" "}
                <a
                  href={`mailto:${giveConfig.contactEmail}`}
                  className="font-semibold text-dc-primary-900 underline decoration-from-font"
                >
                  {giveConfig.contactEmail}
                </a>
                .
              </p>
            </LandingReveal>
          ) : (
            <ul className="divide-y divide-[#ebebeb] overflow-hidden rounded-2xl border border-[#ebebeb] bg-white">
              {accounts.map((account, index) => {
                const meta = getChapterMeta(account.chapterSlug)
                const name = meta?.name ?? account.label

                return (
                  <li key={account.chapterSlug}>
                    <LandingReveal delay={index * 0.05}>
                      <button
                        type="button"
                        onClick={() => setSelected(account)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition hover:bg-[#f7f9fc] sm:px-6"
                      >
                        <div className="min-w-0">
                          <p className="font-outfit text-lg font-semibold tracking-tight text-[#1f1f1f]">
                            {name}
                          </p>
                          <p className="mt-1 font-dm-sans text-sm text-[#5a5a5a]">
                            {givingMethodsLabel(account)}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1.5 font-dm-sans text-sm font-semibold text-dc-primary-900">
                          View details
                          <IconArrowRight size={16} stroke={2} />
                        </span>
                      </button>
                    </LandingReveal>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#00205c] px-4 py-16 md:px-8 md:py-24 lg:px-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(158,126,67,0.18),transparent_60%)]" />
        {!prefersReducedMotion && (
          <motion.div
            className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-dc-primary-900/15 blur-[100px]"
            animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.5, 0.35] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        <LandingReveal className="relative mx-auto max-w-3xl text-center">
          <blockquote className="font-outfit text-2xl font-medium leading-snug tracking-tight text-white md:text-[1.75rem] md:leading-[1.35]">
            &ldquo;Give, and it will be given to you. A good measure, pressed down, shaken together and running
            over, will be poured into your lap. For with the measure you use, it will be measured to you.&rdquo;
          </blockquote>
          <p className="mt-5 font-dm-sans text-sm tracking-wide text-[#7a92b0]">Luke 6:38</p>
        </LandingReveal>
      </section>

      <LandingFooter />

      <GiveChapterDetailsModal
        open={Boolean(selected)}
        account={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

export default GivePage
